import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import SelectComp from "components/common/Select/SelectComp";
import {
  SectionHeading,
  Subheading as SubheadingBase,
} from "components/misc/Headings.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import EmailIllustrationSrc from "images/email-illustration.svg";
import { SECTION_SPACING_IN_REM } from "config/layout_config";
import { useFormik } from "formik";
import * as Yup from "yup";
import Error from "components/error/Error";
const Container = tw.div`relative`;
const TwoColumn = styled.div`
  padding-top: ${SECTION_SPACING_IN_REM}rem;
  ${tw`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto`}
`;

const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 flex-shrink-0 h-80 md:h-auto`;
const TextColumn = styled(Column)((props) => [
  tw`md:w-7/12 mt-16 md:mt-0`,
  props.textOnLeft
    ? tw`md:mr-12 lg:mr-16 md:order-first`
    : tw`md:ml-12 lg:ml-16 md:order-last`,
]);

const Image = styled.div((props) => [
  `background-image: url("${props.imageSrc}");`,
  tw`rounded bg-contain bg-no-repeat bg-center h-full`,
]);
const TextContent = tw.div` text-center md:text-left`;

const Subheading = tw(SubheadingBase)`text-center md:text-left`;
const Heading = tw(
  SectionHeading
)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const Description = tw.p`mt-4 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`;

const Form = tw.form`mt-8 md:mt-10 text-sm flex flex-col max-w-sm mx-auto md:mx-0`;

export const Input = tw.input`mt-6 first:mt-0 border-b-2 py-3 focus:outline-none font-medium transition duration-300 hocus:border-primary-500`;

const Textarea = styled(Input).attrs({ as: "textarea" })`
  ${tw`h-24`}
`;

const SubmitButton = tw(PrimaryButtonBase)`inline-block mt-8`;

export default ({
  subheading = "Submit a Project or Property",
  heading = (
    <>
      Submit your property or{" "}
      <span tw="text-primary-500">real estate project</span>
      <wbr /> and discover how we can fractionalize it for you.
    </>
  ),
  description = (
    <>
      Our team is ready to fractionalize your property on our Marketplace.
      Mulkinvest accepts commercial properties from across Kyrgyzstan and we are
      always looking to assist sponsors in leveraging our network to raise
      capital. Please complete the form to submit your property or project.
      <br />
      <br />
      <span tw="text-primary-500">
        Benefits of Submitting a Property or Project
      </span>
      <wbr />
      <br />
      <br /> - Property owners who want to recapitalize their assets can have
      the benefit of not needing to fully sell their properties while
      maintaining management of the asset and collecting management fees.
      <br />
      <br /> - Project sponsors and developers can raise capital for their
      developments efficiently from a diversified base of stakeholders.
      <br />
      <br /> - You can receive support from the Mulkinvest team to tailor your
      offerings and maximize impact.
      <br />
      <br />{" "}
      <span tw="text-primary-500">Please fill out the following form:</span>
    </>
  ),
  submitButtonText = "Send",
  formAction = "#",
  formMethod = "get",
  textOnLeft = true,
}) => {
  // The textOnLeft boolean prop can be used to display either the text on left or right side of the image.
  const formik = useFormik({
    initialValues: {
      email: "",
      name: "",
      phoneNumber: "",
      projectName: "",
      projectLocation: "",
      projectDescription: "",
    },
    validationSchema: Yup.object({
      email: Yup.string().required("Required"),
      name: Yup.string().required("Required"),
      phoneNumber: Yup.number()
        .typeError("Phone number must contain only digits")
        .required("Required"),
      projectName: Yup.string().required("Required"),
      projectLocation: Yup.string().required("Required"),
      projectDescription: Yup.string().required("Required"),
    }),
    onSubmit: (values) => {
      alert(JSON.stringify(values, null, 2));
    },
  });
  return (
    <Container id="submitProperty">
      <TwoColumn>
        <ImageColumn>
          <Image imageSrc={EmailIllustrationSrc} />
        </ImageColumn>
        <TextColumn textOnLeft={textOnLeft}>
          <TextContent>
            {subheading && <Subheading>{subheading}</Subheading>}
            <Heading>{heading}</Heading>
            {description && <Description>{description}</Description>}
            <Form onSubmit={formik.handleSubmit}>
              <Input
                value={formik.values.name}
                onChange={formik.handleChange}
                type="text"
                name="name"
                placeholder="Full Name"
              />
              {formik.errors.name && formik.touched.name ? (
                <Error errorName={formik.errors.name} />
              ) : null}
              <Input
                type="text"
                name="phoneNumber"
                errors={formik.errors.phoneNumber}
                placeholder="Phone Number"
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
              />
              {formik.errors.phoneNumber ? (
                <Error errorName={formik.errors.phoneNumber} />
              ) : null}
              <Input
                type="email"
                name="email"
                placeholder="Your Email Address"
                value={formik.values.email}
                onChange={formik.handleChange}
              />{" "}
              {formik.errors.email && formik.touched.email ? (
                <Error errorName={formik.errors.email} />
              ) : null}
              <Input
                type="text"
                name="projectName"
                value={formik.values.projectName}
                onChange={formik.handleChange}
                placeholder="Property/Project Name"
              />{" "}
              {formik.errors.projectName && formik.touched.projectName ? (
                <Error errorName={formik.errors.projectName} />
              ) : null}
              <Input
                type="text"
                value={formik.values.projectLocation}
                onChange={formik.handleChange}
                name="projectLocation"
                placeholder="Property/Project Location"
              />{" "}
              {formik.errors.projectLocation &&
                formik.touched.projectLocation ? (
                <Error errorName={formik.errors.projectLocation} />
              ) : null}
              <Textarea
                name="projectDescription"
                value={formik.values.projectDescription}
                onChange={formik.handleChange}
                placeholder="Enter a description of your property"
              />{" "}
              {formik.errors.projectDescription &&
                formik.touched.projectDescription ? (
                <Error errorName={formik.errors.projectDescription} />
              ) : null}
              <SubmitButton type="submit">{submitButtonText}</SubmitButton>
            </Form>
          </TextContent>
        </TextColumn>
      </TwoColumn>
    </Container>
  );
};
